import React, { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { api } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { useCart } from '@/lib/cart';
import { toast } from 'sonner';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { SHOP } from '@/constants/testIds';
import SEO from '@/components/SEO';

const ProductDetail = () => {
  const { slug } = useParams();
  const { user } = useAuth();
  const { refresh: refreshCart } = useCart();
  const nav = useNavigate();
  const [p, setP] = useState(null);
  const [concerns, setConcerns] = useState([]);
  const [qty, setQty] = useState(1);
  const [adding, setAdding] = useState(false);

  useEffect(() => {
    api.get(`/shop/products/${slug}`).then(({ data }) => setP(data)).catch(() => nav('/products', { replace: true }));
    api.get('/content/health-concerns').then(({ data }) => setConcerns(data));
  }, [slug, nav]);

  const addToCart = async () => {
    if (!user) { nav(`/login?next=${encodeURIComponent(`/products/${slug}`)}`); return; }
    setAdding(true);
    try {
      await api.post('/shop/cart/add', { product_id: p.product_id, quantity: qty });
      await refreshCart();
      toast.success('Added to cart');
    } catch (e) {
      toast.error('Could not add to cart');
    } finally {
      setAdding(false);
    }
  };

  if (!p) return <div className="sans-container sans-section text-sans_mute">Loading…</div>;

  // Compose intelligent SEO fallbacks. If admin didn't set seo_title/description,
  // we synthesize compelling values from the product data itself.
  const seoTitle = p.seo_title?.trim()
    || `${p.name} — ${p.tagline || 'Authentic Ayurvedic formulation'} | Saans Ayurved`;
  const seoDescriptionParts = [
    p.tagline,
    p.size && `${p.size}.`,
    (p.benefits?.[0] || p.description?.slice(0, 160)),
    'AYUSH-certified. Shipped across India from Dehradun.',
  ].filter(Boolean);
  const seoDescription = p.seo_description?.trim()
    || seoDescriptionParts.join(' ').slice(0, 158);

  // Rich Product schema for Google — brand, offers, availability, sku, category, reviews-ready.
  const priceValidUntil = new Date(Date.now() + 365 * 24 * 3600 * 1000).toISOString().slice(0, 10);
  const productSchema = {
    '@context': 'https://schema.org',
    '@type': 'Product',
    name: p.name,
    description: p.description,
    image: (p.gallery && p.gallery.length ? p.gallery : [p.image]).filter(Boolean),
    sku: p.product_id,
    mpn: p.slug,
    category: p.category || 'Ayurvedic Medicine',
    brand: { '@type': 'Brand', name: 'Saans Ayurved' },
    manufacturer: { '@type': 'Organization', name: 'Swarnimakash Lifesciences Pvt Ltd' },
    offers: {
      '@type': 'Offer',
      url: `https://saansayurved.com/products/${p.slug}`,
      priceCurrency: 'INR',
      price: p.price,
      priceValidUntil,
      availability: (p.stock ?? 1) > 0
        ? 'https://schema.org/InStock'
        : 'https://schema.org/OutOfStock',
      itemCondition: 'https://schema.org/NewCondition',
      seller: { '@type': 'Organization', name: 'Saans Ayurved' },
    },
  };

  return (
    <div className="bg-sans_bone">
      <SEO title={seoTitle} description={seoDescription} image={p.image} type="product" schema={productSchema} />
      <section className="sans-section">
        <div className="sans-container grid md:grid-cols-2 gap-12 md:gap-16">
          <div>
            <div className="aspect-square bg-sans_bone border border-sans_line overflow-hidden flex items-center justify-center p-8 md:p-12">
              <img src={p.image} alt={p.name} className="max-w-full max-h-full object-contain" />
            </div>
            {p.gallery?.length > 1 && (
              <div className="mt-4 grid grid-cols-4 gap-2">
                {p.gallery.slice(0, 4).map((g, i) => (
                  <div key={i} className="aspect-square bg-sans_bone border border-sans_line overflow-hidden flex items-center justify-center p-2">
                    <img src={g} alt="" className="max-w-full max-h-full object-contain" />
                  </div>
                ))}
              </div>
            )}
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-sans_mute">{p.size}</div>
            <h1 className="font-serif text-4xl md:text-5xl mt-2 text-sans_ink tracking-tight">{p.name}</h1>
            <div className="text-sans_mute mt-2">{p.tagline}</div>

            {p.tags?.length > 0 && concerns.length > 0 && (
              <div className="mt-5 flex flex-wrap gap-2">
                {p.tags.map((tag) => {
                  const c = concerns.find((x) => x.slug === tag);
                  if (!c) return null;
                  const accent = p.accent || c.color;
                  return (
                    <Link
                      key={tag}
                      to={`/products?concern=${tag}`}
                      className="inline-flex items-center gap-2 px-3 py-1.5 text-[10px] uppercase tracking-[0.2em] border transition-all hover:text-white"
                      style={{
                        borderColor: accent,
                        color: accent,
                        backgroundColor: 'transparent',
                      }}
                      onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = accent; e.currentTarget.style.color = '#fff'; }}
                      onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; e.currentTarget.style.color = accent; }}
                    >
                      <span aria-hidden className="w-1.5 h-1.5 rounded-full" style={{ background: 'currentColor' }} />
                      For {c.name}
                    </Link>
                  );
                })}
              </div>
            )}

            <div className="mt-6 flex items-baseline gap-4">
              <div className="font-serif text-3xl text-sans_ink">₹{p.price}</div>
              {p.compare_at && p.compare_at > p.price && (
                <div className="text-sm text-sans_mute line-through">₹{p.compare_at}</div>
              )}
            </div>
            <p className="mt-8 text-sans_ink leading-relaxed">{p.description}</p>

            <div className="mt-10 flex items-center gap-4">
              <div className="flex items-center border border-sans_line">
                <button onClick={() => setQty((q) => Math.max(1, q - 1))} className="w-10 h-11 text-sans_ink hover:bg-sans_bone">−</button>
                <div className="w-10 text-center text-sm">{qty}</div>
                <button onClick={() => setQty((q) => Math.min(20, q + 1))} className="w-10 h-11 text-sans_ink hover:bg-sans_bone">+</button>
              </div>
              <button onClick={addToCart} disabled={adding} data-testid={SHOP.addToCartBtn} className="sans-btn-primary flex-1">
                {adding ? 'Adding…' : 'Add to cart'}
              </button>
            </div>

            <div className="mt-12">
              <Accordion type="single" collapsible defaultValue="ingredients">
                <AccordionItem value="ingredients" className="border-b border-sans_line">
                  <AccordionTrigger className="font-serif text-lg text-sans_ink py-5 hover:no-underline">Ingredients</AccordionTrigger>
                  <AccordionContent className="text-sm text-sans_mute leading-relaxed pb-5">
                    <ul className="space-y-1.5">
                      {p.ingredients?.map((i, k) => <li key={k}>— {i}</li>)}
                    </ul>
                  </AccordionContent>
                </AccordionItem>
                <AccordionItem value="benefits" className="border-b border-sans_line">
                  <AccordionTrigger className="font-serif text-lg text-sans_ink py-5 hover:no-underline">Benefits</AccordionTrigger>
                  <AccordionContent className="text-sm text-sans_mute leading-relaxed pb-5">
                    <ul className="space-y-1.5">
                      {p.benefits?.map((b, k) => <li key={k}>— {b}</li>)}
                    </ul>
                  </AccordionContent>
                </AccordionItem>
                <AccordionItem value="directions" className="border-b border-sans_line">
                  <AccordionTrigger className="font-serif text-lg text-sans_ink py-5 hover:no-underline">Directions</AccordionTrigger>
                  <AccordionContent className="text-sm text-sans_mute leading-relaxed pb-5">{p.directions}</AccordionContent>
                </AccordionItem>
                <AccordionItem value="safety" className="border-b border-sans_line">
                  <AccordionTrigger className="font-serif text-lg text-sans_ink py-5 hover:no-underline">Safety information</AccordionTrigger>
                  <AccordionContent className="text-sm text-sans_mute leading-relaxed pb-5">{p.safety}</AccordionContent>
                </AccordionItem>
              </Accordion>
            </div>

            <div className="mt-10 border-t border-sans_line pt-6 text-xs text-sans_mute leading-relaxed">
              For personalized guidance, please <Link to="/consultation" className="sans-link">consult a qualified Ayurvedic doctor</Link>.
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ProductDetail;
